/**
 * Datum/Tijd formatting utilities voor Belgische notatie
 * Alle datums: dd/mm/yyyy
 * Alle tijden: 24-uurs formaat (HH:mm)
 */

const LOCALE = 'nl-BE';

/**
 * Format datum naar Belgisch formaat
 * @param {string|Date} dateStr - Datum string of Date object
 * @param {object} options - Extra opties voor toLocaleDateString
 * @returns {string} Geformatteerde datum
 */
export const formatDate = (dateStr, options = {}) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  const defaultOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString(LOCALE, defaultOptions);
};

/**
 * Format datum met dag en maand naam
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Geformatteerde datum (bijv. "maandag 15 januari 2025")
 */
export const formatDateLong = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString(LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Format datum kort (dag + maand)
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Geformatteerde datum (bijv. "15 jan")
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short'
  });
};

/**
 * Format alleen de maand (kort)
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Maand afkorting (bijv. "jan")
 */
export const formatMonth = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString(LOCALE, { month: 'short' });
};

/**
 * Format alleen de dag
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Dag nummer
 */
export const formatDay = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString(LOCALE, { day: 'numeric' });
};

/**
 * Format tijd naar 24-uurs formaat
 * @param {string|Date} timeStr - Tijd string of Date object
 * @returns {string} Geformatteerde tijd (bijv. "14:30")
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  
  // Als het een tijd string is (bijv. "14:30:00")
  if (typeof timeStr === 'string' && timeStr.match(/^\d{1,2}:\d{2}/)) {
    const parts = timeStr.split(':');
    return `${parts[0].padStart(2, '0')}:${parts[1]}`;
  }
  
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format datum en tijd samen
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Geformatteerde datum en tijd (bijv. "15/01/2025 14:30")
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleString(LOCALE, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format datum en tijd met dag/maand naam
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Geformatteerde datum en tijd (bijv. "15 jan 2025, 14:30")
 */
export const formatDateTimeShort = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format prijs naar Belgisch Euro formaat
 * @param {number} price - Prijs in euro
 * @returns {string} Geformatteerde prijs (bijv. "€ 14,99")
 */
export const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '€ 0,00';
  
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

/**
 * Relatieve tijd (bijv. "2 uur geleden")
 * @param {string|Date} dateStr - Datum string of Date object
 * @returns {string} Relatieve tijd
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return `${diffMins} min geleden`;
  if (diffHours < 24) return `${diffHours} uur geleden`;
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  
  return formatDate(dateStr);
};
